import {
  Autocomplete,
  Checkbox,
  TextField,
  Divider,
  Stack,
  Grid,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@material-ui/core'
import { useState, useEffect } from 'react'
import commonService from 'src/components/chatbot/services/commonService'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import { fDateTime } from 'src/utils/formatTime'
import { runTypeMapping } from 'src/views/FileUploadView/utils/fileUtils'

function SamplesFilter({ selectedSamples = [], setSelectedSamples }) {
  const [input, setInput] = useState('')
  const [sampleOptions, setSampleOptions] = useState([])
  const [filteredOptions, setFilteredOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [hoveredOption, setHoveredOption] = useState(null)

  const handleFetchSamples = async () => {
    setLoading(true)
    try {
      const results = await commonService.fetchAllSamples()
      setSampleOptions(results)
      setFilteredOptions(results)
    } catch (error) {
      console.error('Error fetching samples:', error)
      setSampleOptions([])
      setFilteredOptions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    handleFetchSamples()
  }, [])

  useEffect(() => {
    if (!input.trim()) {
      setFilteredOptions(sampleOptions)
    } else {
      const filtered = sampleOptions.filter(
        (sample) =>
          (sample.name && sample.name.toLowerCase().includes(input.toLowerCase())) ||
          (sample.filename && sample.filename.toLowerCase().includes(input.toLowerCase())) ||
          (sample.id && sample.id.toLowerCase().includes(input.toLowerCase())),
      )
      setFilteredOptions(filtered)
    }
  }, [input, sampleOptions])

  const getOptionLabel = (option) => {
    if (option === 'select-all-option') return ''
    if (!option) return ''
    if (typeof option === 'string') return option

    return option.label || option.name
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      const newSelectedSamples = [...selectedSamples]
      filteredOptions.forEach((sample) => {
        if (!selectedSamples.some((s) => s?.value === sample?.id)) {
          newSelectedSamples.push({
            value: sample.id,
            label: sample.name,
            name: sample.name,
            id: sample.id,
            filename: sample.filename,
            createdAt: sample.createdAt,
            analysisType: sample.analysisType,
          })
        }
      })
      setSelectedSamples(newSelectedSamples)
    } else {
      const filteredSamples = selectedSamples.filter((sample) => !filteredOptions.some((o) => o?.id === sample?.value))
      setSelectedSamples(filteredSamples)
    }
  }

  const areAllCurrentResultsSelected = () => {
    return (
      filteredOptions.length > 0 &&
      filteredOptions.every((sample) => selectedSamples.some((s) => s?.value === sample?.id))
    )
  }

  const getOptions = () => {
    if (filteredOptions.length === 0) return []
    return ['select-all-option', ...filteredOptions]
  }

  const handleClearInput = () => {
    setInput('')
    setFilteredOptions(sampleOptions)
  }

  // Helper function to get analysis type display name using the correct mapping
  const getAnalysisTypeDisplay = (analysisType) => {
    if (!analysisType) return null

    // Use the mapping from fileUtils.js
    if (runTypeMapping[analysisType]) {
      return runTypeMapping[analysisType][1] // Use the short display name
    }

    // Fallback for other analysis types
    return analysisType
  }

  // Helper function to get correct filenames based on file type
  const getFilenames = (sample) => {
    if (!sample.originalData) return null

    const originalData = sample.originalData

    // Check if it's a FASTQ pair
    if (originalData.fastq_pair_id && originalData.fastq_file_1 && originalData.fastq_file_2) {
      return {
        type: 'fastq',
        filenames: [originalData.fastq_file_1.name, originalData.fastq_file_2.name],
        displayText: `${originalData.fastq_file_1.name}, ${originalData.fastq_file_2.name}`,
      }
    }

    // Check if it's a VCF file
    if (originalData.name) {
      return {
        type: 'vcf',
        filenames: [originalData.name],
        displayText: originalData.name,
      }
    }

    // Fallback to the formatted filename
    return {
      type: 'unknown',
      filenames: [sample.filename],
      displayText: sample.filename,
    }
  }

  // Helper function to truncate text and create tooltip
  const truncateWithTooltip = (text, maxLength = 15) => {
    if (!text || text.length <= maxLength) {
      return { displayText: text, tooltip: null }
    }

    const truncated = text.substring(0, maxLength) + '...'
    return { displayText: truncated, tooltip: text }
  }

  // Helper function to render filename chips with truncation and tooltip
  const renderFilenameChips = (sample) => {
    const filenameData = getFilenames(sample)
    if (!filenameData) return null

    if (filenameData.type === 'fastq') {
      // Render two separate chips for FASTQ pairs
      return filenameData.filenames.map((filename, index) => {
        const { displayText, tooltip } = truncateWithTooltip(filename, 15)

        const chip = (
          <Chip
            key={index}
            label={displayText}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: 24,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              borderColor: 'divider',
              color: 'text.primary',
            }}
          />
        )

        if (tooltip) {
          return (
            <Tooltip
              key={index}
              title={
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {tooltip}
                </Typography>
              }
              placement="top"
              arrow
            >
              {chip}
            </Tooltip>
          )
        }

        return chip
      })
    } else {
      // Render single chip for VCF or other files
      const { displayText, tooltip } = truncateWithTooltip(filenameData.filenames[0], 15)

      const chip = (
        <Chip
          label={displayText}
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.7rem',
            height: 24,
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            borderColor: 'divider',
            color: 'text.primary',
          }}
        />
      )

      if (tooltip) {
        return (
          <Tooltip
            title={
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {tooltip}
              </Typography>
            }
            placement="top"
            arrow
          >
            {chip}
          </Tooltip>
        )
      }

      return chip
    }
  }

  // Helper function to format date only (no time)
  const formatDateOnly = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  return (
    <Stack direction="column" spacing={2} divider={<Divider />}>
      {selectedSamples !== null && setSelectedSamples && (
        <>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Selected Samples ({selectedSamples.length})
              </Typography>
              {selectedSamples.length > 0 && (
                <IconButton size="small" onClick={() => setSelectedSamples([])} title="Clear all samples">
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxHeight: '300px',
                overflowY: 'auto',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              {selectedSamples.length > 0 ? (
                selectedSamples.map((sample) => (
                  <Box
                    key={sample.value || sample.id}
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    {/* Sample name as main text */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {sample.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newSamples = selectedSamples.filter(
                            (s) => (s.value || s.id) !== (sample.value || sample.id),
                          )
                          setSelectedSamples(newSamples)
                        }}
                        sx={{ ml: 1 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Information chips */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {/* Row 1: Filename chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{renderFilenameChips(sample)}</Box>

                      {/* Row 2: Upload time chip */}
                      {sample.createdAt && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label={formatDateOnly(sample.createdAt)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: 24,
                              backgroundColor: 'rgba(0, 0, 0, 0.08)',
                              borderColor: 'divider',
                              color: 'text.primary',
                            }}
                          />
                        </Box>
                      )}

                      {/* Row 3: Analysis type chip */}
                      {sample.analysisType && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label={getAnalysisTypeDisplay(sample.analysisType)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: 24,
                              backgroundColor: 'rgba(0, 0, 0, 0.08)',
                              borderColor: 'divider',
                              color: 'text.primary',
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No samples selected
                </Typography>
              )}
            </Box>
          </Box>
          <Autocomplete
            multiple
            options={getOptions()}
            loading={loading}
            disableCloseOnSelect
            disableClearable
            filterOptions={(x) => x}
            inputValue={input}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'reset' || reason === 'blur') return
              setInput(newInputValue)
            }}
            clearOnBlur={false}
            blurOnSelect={false}
            renderInput={(params) => {
              const { InputProps, ...rest } = params
              return (
                <Grid sx={{ display: 'flex' }}>
                  <TextField
                    {...rest}
                    label="Search samples by name, filename, or ID"
                    variant="outlined"
                    placeholder="Type to search samples..."
                    InputProps={{
                      ...InputProps,
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {InputProps.endAdornment}
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {input.length > 0 && (
                            <IconButton
                              size="small"
                              aria-label="Clear search input"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleClearInput()
                              }}
                              tabIndex={0}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
              )
            }}
            renderOption={(props, option, { selected }) => (
              <li
                {...props}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
                style={{
                  transition: 'background-color 0.2s',
                }}
              >
                {option === 'select-all-option' ? (
                  <Box
                    sx={{
                      width: '100%',
                      borderBottom: 1,
                      borderColor: 'divider',
                      pb: 1,
                      backgroundColor: hoveredOption === option ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={areAllCurrentResultsSelected()}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{
                          '&:hover': { color: 'primary.main' },
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: areAllCurrentResultsSelected() ? 'primary.main' : 'inherit',
                        }}
                      >
                        Select All Results ({filteredOptions.length})
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      backgroundColor: hoveredOption === option ? 'action.hover' : 'transparent',
                      transition: 'background-color 0.2s',
                      p: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={Array.isArray(selectedSamples) && selectedSamples.some((s) => s?.value === option?.id)}
                        sx={{
                          '&:hover': { color: 'primary.main' },
                        }}
                      />
                      <Box sx={{ flex: 1, ml: 1 }}>
                        {/* Sample name as main text */}
                        <Typography
                          sx={{
                            fontWeight: selectedSamples.some((s) => s?.value === option?.id) ? 600 : 400,
                            color: selectedSamples.some((s) => s?.value === option?.id) ? 'primary.main' : 'inherit',
                            mb: 0.5,
                          }}
                        >
                          {option?.name}
                        </Typography>

                        {/* Information chips */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {/* Row 1: Filename chips */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{renderFilenameChips(option)}</Box>

                          {/* Row 2: Upload time chip */}
                          {option?.createdAt && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              <Chip
                                label={formatDateOnly(option.createdAt)}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 20,
                                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                  borderColor: 'divider',
                                  color: 'text.primary',
                                }}
                              />
                            </Box>
                          )}

                          {/* Row 3: Analysis type chip */}
                          {option?.analysisType && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              <Chip
                                label={getAnalysisTypeDisplay(option.analysisType)}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 20,
                                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                  borderColor: 'divider',
                                  color: 'text.primary',
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </li>
            )}
            getOptionLabel={getOptionLabel}
            value={selectedSamples || []}
            onChange={(_, val) => {
              const filteredVal = val.filter((v) => v !== 'select-all-option')
              const formattedVal = filteredVal.map((sample) => {
                if (sample.value) return sample // Already formatted
                return {
                  value: sample.id,
                  label: sample.name,
                  name: sample.name,
                  id: sample.id,
                  filename: sample.filename,
                  createdAt: sample.createdAt,
                  analysisType: sample.analysisType,
                }
              })
              setSelectedSamples(formattedVal)
            }}
            isOptionEqualToValue={(option, value) => {
              if (option === 'select-all-option') return false
              return option?.id === value?.value || option?.id === value?.id
            }}
            noOptionsText={
              <Box sx={{ py: 2, textAlign: 'center' }}>
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography color="text.secondary">
                    {sampleOptions.length === 0 ? 'No samples available' : 'No results found'}
                  </Typography>
                )}
              </Box>
            }
            ListboxProps={{
              style: {
                maxHeight: '400px',
              },
            }}
          />
        </>
      )}
    </Stack>
  )
}

export default SamplesFilter
